/**
 * Read-aloud through the browser's own `speechSynthesis` (UC-A6).
 *
 * ## Why this is the primary engine now
 *
 * The server-side engine (VODER, Piper, any OpenAI-compatible TTS) works, but it needs a GPU the
 * assistant is already using to answer with, a container in the compose file, a model download, and
 * — in VODER's case — an AGPL decision from the bank. `speechSynthesis` needs none of that: it is
 * in every browser an employee already has, it costs the deployment nothing, and it is the same
 * feature. So the browser is tried first and the server is the fallback for a language the
 * workstation has no voice for, which in practice means Arabic on a stripped Windows image.
 *
 * ## Local voices are preferred, not required
 *
 * `SpeechSynthesisVoice.localService` distinguishes a voice that synthesises on the machine from
 * one the browser fetches — and a remote voice means the answer text goes to the vendor behind it.
 * Local voices are therefore preferred when the workstation has one: they are also faster, work
 * offline, and do not stall behind a network round trip. Where there is no local voice for the
 * answer's language, a remote one is used rather than the feature being absent. That is NAB's call
 * and it is recorded in `docs/adr/0008-voice-input-and-read-aloud.md`.
 *
 * ## The two things that make this API awkward
 *
 * **The voice list is empty on first call.** Chrome populates it asynchronously and fires
 * `voiceschanged` when it is ready, so anything that reads `getVoices()` on mount sees nothing and
 * concludes the browser cannot speak. :func:`loadVoices` waits for the event, with a timeout,
 * because the event never fires at all in a browser whose list was already populated.
 *
 * **A long utterance stops on its own.** Chrome's synthesiser gives up partway through anything
 * much over about fifteen seconds. The caller splits the answer into sentences and this speaks them
 * in order, which is the same shape the server path already uses for a different reason.
 */

/** How long to wait for `voiceschanged` before concluding the list is what it is. */
const VOICE_LOAD_TIMEOUT_MS = 1500;

export interface BrowserSpeechHandle {
  /** Stop immediately and drop anything still queued. */
  cancel: () => void;
}

export function browserSpeechAvailable(): boolean {
  return typeof window !== "undefined" && "speechSynthesis" in window;
}

/**
 * The installed voices, waiting for the browser to populate them if it has not yet.
 *
 * Resolves with whatever is available when the timeout expires rather than rejecting: an empty
 * list is a real answer ("this machine has no voices"), and the caller falls back to the server.
 */
export async function loadVoices(): Promise<SpeechSynthesisVoice[]> {
  if (!browserSpeechAvailable()) return [];

  const existing = window.speechSynthesis.getVoices();
  if (existing.length > 0) return existing;

  return new Promise((resolve) => {
    let settled = false;
    const finish = (): void => {
      if (settled) return;
      settled = true;
      window.speechSynthesis.removeEventListener("voiceschanged", finish);
      resolve(window.speechSynthesis.getVoices());
    };
    window.speechSynthesis.addEventListener("voiceschanged", finish);
    window.setTimeout(finish, VOICE_LOAD_TIMEOUT_MS);
  });
}

/**
 * The best voice for a language, or null when the machine has none.
 *
 * `en` matches `en-GB` and `en-US`; `ar` matches `ar-SA`, `ar-EG` and the rest. Matching on the
 * prefix is the point — an employee answering in Arabic should be read to in Arabic whichever
 * regional voice the workstation happens to ship, and a fallback to an English voice reading
 * Arabic text produces sounds rather than words.
 */
export function pickVoice(
  voices: SpeechSynthesisVoice[],
  language: string,
): SpeechSynthesisVoice | null {
  const prefix = language.toLowerCase().split("-")[0] ?? "";
  const matching = voices.filter((voice) =>
    voice.lang.toLowerCase().replace("_", "-").startsWith(prefix),
  );
  if (matching.length === 0) return null;

  // Local first — see the module docstring. `default` breaks the remaining tie, because it is the
  // voice the employee has already chosen to hear elsewhere on their machine.
  const ordered = [...matching].sort((a, b) => {
    if (a.localService !== b.localService) return a.localService ? -1 : 1;
    if (a.default !== b.default) return a.default ? -1 : 1;
    return 0;
  });
  return ordered[0] ?? null;
}

export interface SpeakInBrowserOptions {
  /** Spoken in order. Split by the caller, because Chrome abandons a long utterance. */
  pieces: string[];
  voice: SpeechSynthesisVoice;
  language: string;
  onDone: () => void;
  onError: (reason: string) => void;
}

/**
 * Speak the pieces in order, returning a handle that stops them.
 *
 * Sequenced by chaining each utterance's `onend` rather than queueing them all at once: queueing
 * works until `cancel` is called, at which point Chrome fires `onend` for every pending utterance
 * and a naive "all done" check reports success for an answer nobody heard.
 */
export function speakInBrowser({
  pieces,
  voice,
  language,
  onDone,
  onError,
}: SpeakInBrowserOptions): BrowserSpeechHandle {
  let cancelled = false;
  let index = 0;

  const next = (): void => {
    if (cancelled) return;
    if (index >= pieces.length) {
      onDone();
      return;
    }

    const utterance = new SpeechSynthesisUtterance(pieces[index]!);
    utterance.voice = voice;
    // Set alongside the voice: some engines read `lang` rather than the voice for prosody, and an
    // Arabic string spoken with English prosody is hard to follow even in the right voice.
    utterance.lang = voice.lang || language;
    utterance.onend = () => {
      index += 1;
      next();
    };
    utterance.onerror = (event) => {
      // `interrupted` and `canceled` are what a deliberate stop produces. Not failures.
      if (
        cancelled ||
        event.error === "interrupted" ||
        event.error === "canceled"
      )
        return;
      cancelled = true;
      onError(event.error ?? "unknown");
    };
    window.speechSynthesis.speak(utterance);
  };

  next();

  return {
    cancel: () => {
      cancelled = true;
      // Guarded: this runs from a React cleanup, which can fire after the page has begun tearing
      // down and `speechSynthesis` is no longer reachable.
      if (browserSpeechAvailable()) window.speechSynthesis.cancel();
    },
  };
}
